import {
  FANNIE_MAE_CLASS_OPTIONS,
  FREDDIE_MAC_CLASS_OPTIONS,
  PROJECT_DESIGN_TYPE_OPTIONS,
} from './constants';

type CondoInformationFieldsOptions = {
  watch: (field: string) => unknown;
  handleChange: (field: string, value: unknown) => void;
  editExpirationDate: number;
  isCooperative: boolean;
  isLoanProductAdvisor: boolean;
  isConventional: boolean;
};

export const getCondoInformationFields = ({
  watch,
  handleChange,
  editExpirationDate,
  isCooperative,
  isLoanProductAdvisor,
  isConventional,
}: CondoInformationFieldsOptions) => [
  {
    type: 'text',
    name: 'condoProjectName',
    label: isCooperative ? 'Cooperative Company Name' : 'Project Name',
    dataTestId: 'condoProjectName',
    placeholder: isCooperative ? 'Cooperative Company Name' : 'Project Name',
    span: 2,
  },
  {
    type: 'text',
    name: 'cpmProjectId',
    label: 'CPM Project ID',
    dataTestId: 'cpmProjectId',
    placeholder: 'CPM Project ID',
    span: 2,
  },
  {
    type: 'text',
    name: 'hoaTaxId',
    label: isCooperative ? 'Co-op Tax ID #' : 'HOA Tax ID #',
    dataTestId: 'hoaTaxId',
    placeholder: isCooperative ? 'Co-op Tax ID #' : 'HOA Tax ID #',
    span: 1,
    rules: {
      validate: (value: string) => {
        if (!value) return true;
        return (
          (value.length >= 9 && value.length <= 10) ||
          (isCooperative
            ? 'Co-op Tax ID # must be 9-10 characters'
            : 'HOA Tax ID # must be 9-10 characters')
        );
      },
    },
  },
  {
    type: 'text',
    name: isLoanProductAdvisor ? 'projectAssessmentsResultsNum' : 'phaseId',
    label: isLoanProductAdvisor ? 'Project Assessments Results #' : 'Phase ID',
    dataTestId: isLoanProductAdvisor
      ? 'projectAssessmentsResultsNum'
      : 'phaseId',
    placeholder: isLoanProductAdvisor
      ? 'Project Assessments Results #'
      : 'Phase ID',
    span: 2,
    isHidden: () => isCooperative || !isConventional,
  },
  {
    type: 'text',
    name: isLoanProductAdvisor
      ? 'condoProjectWaiverRequestNum'
      : 'fannieCpmCertificationId',
    label: isLoanProductAdvisor
      ? 'Condo Project Waiver Request #'
      : 'Fannie CPM Certification ID',
    dataTestId: isLoanProductAdvisor
      ? 'condoProjectWaiverRequestNum'
      : 'fannieCpmCertificationId',
    placeholder: isLoanProductAdvisor
      ? 'Condo Project Waiver Request #'
      : 'Fannie CPM Certification ID',
    span: 2,
    isHidden: () => isCooperative || !isConventional,
  },
  {
    type: 'divider',
    name: 'divider-1',
    span: 9,
  },
  {
    type: 'select',
    name: 'condoProjectDesignType',
    label: 'Project Design Type',
    dataTestId: 'condoProjectDesignType',
    options: [{ value: '', label: '' }, ...PROJECT_DESIGN_TYPE_OPTIONS],
    onChangeHandler: (value: number) =>
      handleChange('condoProjectDesignType', value),
    span: 1,
  },
  {
    type: 'select',
    name: 'condoFreddieMacClass',
    label: 'Freddie Mac Class',
    dataTestId: 'condoFreddieMacClass',
    options: [{ value: '', label: '' }, ...FREDDIE_MAC_CLASS_OPTIONS],
    onChangeHandler: (value: unknown) =>
      handleChange('condoFreddieMacClass', value),
    span: 1,
  },
  {
    type: 'select',
    name: 'fannieMaeClass',
    label: 'Fannie Mae Class',
    dataTestId: 'fannieMaeClass',
    options: [{ value: '', label: '' }, ...FANNIE_MAE_CLASS_OPTIONS],
    onChangeHandler: (value: unknown) => handleChange('fannieMaeClass', value),
    span: 1,
  },
  {
    type: 'number',
    name: 'condoNumberOfUnits',
    label: '# of Units',
    dataTestId: 'condoNumberOfUnits',
    placeholder: '# of Units',
    value: watch('condoNumberOfUnits'),
    onChangeHandler: (e) => {
      const value = e.target.value ?? '';
      handleChange('condoNumberOfUnits', value);
    },
    span: 1,
  },
  {
    type: 'number',
    name: 'condoNumberOfUnitsSold',
    label: '# of Units Sold',
    dataTestId: 'condoNumberOfUnitsSold',
    placeholder: '# of Units Sold',
    value: watch('condoNumberOfUnitsSold'),
    onChangeHandler: (e) => {
      const value = e.target.value ?? '';
      handleChange('condoNumberOfUnitsSold', value);
    },
    span: 1,
  },
  {
    type: 'checkbox',
    name: 'condoPredominantlyDetached',
    value: 'condoPredominantlyDetached',
    label: 'Condos in the project are predominantly detached',
    dataTestId: 'condoPredominantlyDetached',
    span: 2,
    position: 'center',
    useHooksForm: true,
    checkboxStyle: { transform: 'translateY(.6rem)' },
    isHidden: () => isCooperative,
  },
  {
    type: 'checkbox',
    name: 'condoWarrantable',
    value: 'condoWarrantable',
    label: 'Warrantable',
    dataTestId: 'condoWarrantable',
    span: 1,
    colStart: 8,
    position: 'center',
    useHooksForm: true,
    checkboxStyle: { transform: 'translateY(.6rem)' },
  },
  {
    type: 'divider',
    name: 'divider-2',
    span: 9,
  },
  {
    type: 'datepicker',
    name: 'condoQuestionnaireEffectiveDate',
    label: 'Questionnaire Effective Date',
    dataTestId: 'condoQuestionnaireEffectiveDate',
    span: 3,
  },
  {
    type: 'datepicker',
    name: 'condoMasterInsuranceStartDate',
    label: 'Master Insurance Start Date',
    dataTestId: 'condoMasterInsuranceStartDate',
    span: 3,
  },
  {
    type: 'datepicker',
    name: 'condoBudgetStartDate',
    label: 'Budget Start Date',
    dataTestId: 'condoBudgetStartDate',
    span: 3,
  },
  {
    type: 'datepicker',
    name: 'condoQuestionnaireExpirationDate',
    label: 'Questionnaire Expiration Date',
    dataTestId: 'condoQuestionnaireExpirationDate',
    readOnly: editExpirationDate !== 1,
    span: 3,
  },
  {
    type: 'datepicker',
    name: 'condoMasterInsuranceEndDate',
    label: 'Master Insurance End Date',
    dataTestId: 'condoMasterInsuranceEndDate',
    span: 3,
  },
  {
    type: 'datepicker',
    name: 'condoBudgetEndDate',
    label: 'Budget End Date',
    dataTestId: 'condoBudgetEndDate',
    span: 3,
  },
  {
    type: 'checkbox',
    name: 'condoEditExpirationDate',
    value: 'condoEditExpirationDate',
    label: 'Edit Expiration Date',
    dataTestId: 'condoEditExpirationDate',
    span: 3,
    useHooksForm: true,
  },
];
